import {
  CreateTaskListInput,
  TaskList,
  TaskListType,
  UpdateTaskListInput,
} from '@akin-home/amplify';
import { append, filterHasValue } from '@akin-home/arrays';
import { removeByID, setByID } from '@akin-home/records';
import { formatNumber } from '@akin-home/utils/strings';
import { v4 as uuid } from 'uuid';
import { CheckListItem, ChecklistModel } from '../types';

/** Creates a checklist model */
export const createChecklist = (
  input: Partial<ChecklistModel> & { principalID: string }
): ChecklistModel => ({
  id: uuid(),
  title: '',
  habitatID: '',
  participants: [],
  participantIDs: [],
  checklist: [],
  ...input,

  type: input.type || TaskListType.Checklist,
});

/** Creates a checklist item */
export const createChecklistItem = (
  input?: Partial<CheckListItem>
): CheckListItem => ({
  id: uuid(),
  title: '',
  checked: false,
  ...input,
});

export const checklistModelToCreateGraphql = (
  checklist: ChecklistModel
): CreateTaskListInput => {
  return {
    title: checklist.title,
    type: checklist.type as TaskListType,
    emoji: checklist.emoji,
    habitatID: checklist.habitatID,
    principalID: checklist.principalID,
    isAkinList: checklist.isAkinList,
    top: checklist.top,
    favourite: checklist.favourite,
    checklist: JSON.stringify(checklist.checklist || []),
    participantIDS: checklist.participantIDs,
    primaryIngredientList: checklist.primaryShoppingList,
  };
};

/** Converts Checklist model to graphql */
export const checklistModelToGraphql = (
  taskItem: ChecklistModel
): UpdateTaskListInput => {
  return {
    ...checklistModelToCreateGraphql(taskItem),
    id: taskItem.id,
  };
};

/** Converts checklist graphql to model */
export const checkListGraphqlToModel = (item: TaskList): ChecklistModel =>
  createChecklist({
    id: item.id as string,
    habitatID: item.habitatID as string,
    title: item.title as string,
    type: item.type as TaskListType,
    emoji: item.emoji || '',
    isAkinList: item.isAkinList || false,
    top: item.top || false,
    favourite: item.favourite || false,
    checklist: item.checklist
      ? JSON.parse(item.checklist).map(createChecklistItem)
      : [],
    principalID: item.principalID as string,
    participantIDs: (item.participantIDS || []) as string[],
    primaryShoppingList: !!item.primaryIngredientList,
    updatedAt: item.updatedAt,
  });

export const formatChecklistsQueryResponse = (
  items: TaskList[]
): ChecklistModel[] => items.map(checkListGraphqlToModel);

export const setChecklistItem =
  (item: CheckListItem) =>
  (list: ChecklistModel): ChecklistModel => {
    return {
      ...list,
      checklist: setByID(list.checklist, item),
    };
  };

export const toggleChecklistItem = (checklist: CheckListItem[], id: string) => {
  return checklist.map((item) =>
    item.id === id ? { ...item, checked: !item.checked } : item
  );
};

export const removeChecklistItem = (id: string) => (item: ChecklistModel) => {
  return {
    ...item,
    checklist: removeByID(item.checklist, id),
  };
};

export const addChecklistItemsFromStrings = (
  list: CheckListItem[],
  titles: string[]
) => {
  return [...list, ...titles.map((title) => createChecklistItem({ title }))];
};

export const addChecklistItem =
  (item?: CheckListItem) => (list: ChecklistModel) => {
    return {
      ...list,
      checklist: append(list.checklist, item || createChecklistItem()),
    };
  };

export const removeChecklistItemByTitle = (
  checklist: CheckListItem[],
  searchStr: string
) => {
  const updatedChecklist = checklist.filter(
    (c) => !c.title || c.title.toLowerCase() !== searchStr
  );
  if (updatedChecklist.length !== checklist.length) {
    return updatedChecklist;
  } else {
    return checklist.filter(
      (c) => !c.title || !c.title.toLowerCase().includes(searchStr)
    );
  }
};

export const parseChecklistItems = (checklist?: string | null) =>
  JSON.parse(checklist || '[]') as CheckListItem[];

/**
 * Sorts checklists by "primary" field and name
 */
export const sortChecklists = (checklists?: ChecklistModel[]) => {
  if (!checklists) return [];

  return [...checklists].sort((a, b) => {
    if (a.primaryShoppingList) return -1;
    if (b.primaryShoppingList) return 1;
    return a.title.toLowerCase() < b.title.toLowerCase() ? -1 : 1;
  });
};

export const setChecklistTitle =
  (title: string) => (checklist: ChecklistModel) => ({ ...checklist, title });

export const setChecklistEmoji =
  (emoji: string) => (checklist: ChecklistModel) => ({
    ...checklist,
    emoji,
  });

export const getChecklistItemQuantityFromRecipes = ({
  recipes,
}: CheckListItem): string | undefined => {
  if (!recipes?.length) return;

  const total_quantity = filterHasValue(
    recipes
      .filter(({ quantity }) => !!quantity) // Has quantity
      .map(({ quantity }) => quantity) // Get the quantities;
  ).reduce((sum, current) => sum + current, 0);

  if (total_quantity) {
    try {
      return formatNumber(total_quantity);
    } catch (ex: any) {
      return;
    }
  }

  return;
};

export const getChecklistItemDisplayText = (item: CheckListItem) => {
  let text = item.title;

  if (item.unit) {
    text = item.unit + ' ' + text;
  }

  const quantity = getChecklistItemQuantityFromRecipes(item);

  if (quantity) {
    text = quantity + ' ' + text;
  }

  return text;
};
